import { useEffect, useMemo, useRef, useState } from "react";

const ITEM_HEIGHT = 56;
const VISIBLE = 5;
const PADDING = ((VISIBLE - 1) / 2) * ITEM_HEIGHT;

/**
 * iOS-style wheel for the post-set ±delta picker.
 *
 * Default range = ±20 lb in 5-lb steps (legacy strength flow). Pass `step`
 * and `unit` to use it for non-strength max types (e.g. speed_mph step 0.1,
 * time_secs step 1).
 */
export default function WheelPicker({ value, onChange, step = 5, unit = "lb", range }) {
    const ref = useRef(null);
    const values = useMemo(() => {
        const span = range ?? step * 4; // ±span in `step` increments → 9 stops
        const out = [];
        for (let v = -span; v <= span + 1e-9; v += step) {
            // round to step precision to dodge fp drift (e.g. 0.30000000000000004)
            out.push(Math.round(v / step) * step);
        }
        return out;
    }, [step, range]);
    const [active, setActive] = useState(value ?? 0);
    const initialized = useRef(false);

    useEffect(() => {
        if (initialized.current) return;
        const idx = values.indexOf(value ?? 0);
        if (idx >= 0 && ref.current) {
            ref.current.scrollTop = idx * ITEM_HEIGHT;
        }
        initialized.current = true;
    }, [value, values]);

    const handleScroll = () => {
        if (!ref.current) return;
        const idx = Math.round(ref.current.scrollTop / ITEM_HEIGHT);
        const next = values[Math.max(0, Math.min(values.length - 1, idx))];
        if (next !== active) {
            setActive(next);
            onChange && onChange(next);
        }
    };

    const pickByIndex = (idx) => {
        if (!ref.current) return;
        ref.current.scrollTo({ top: idx * ITEM_HEIGHT, behavior: "smooth" });
    };

    const fmt = (v) => {
        const rounded = step < 1 ? v.toFixed(1).replace(/\.0$/, "") : String(v);
        return v > 0 ? `+${rounded}` : rounded;
    };

    return (
        <div className="relative w-full max-w-xs mx-auto select-none">
            <div
                className="absolute left-0 right-0 pointer-events-none border-y border-brand-red/40 bg-brand-red/5"
                style={{ top: PADDING, height: ITEM_HEIGHT }}
            />
            <div
                ref={ref}
                data-testid="wheel-picker"
                onScroll={handleScroll}
                className="overflow-y-auto snap-y snap-mandatory no-scrollbar"
                style={{ height: ITEM_HEIGHT * VISIBLE, scrollbarWidth: "none" }}
            >
                <div style={{ paddingTop: PADDING, paddingBottom: PADDING }}>
                    {values.map((v, i) => {
                        const isActive = v === active;
                        return (
                            <button
                                key={v}
                                type="button"
                                data-testid={`wheel-option-${v}`}
                                onClick={() => pickByIndex(i)}
                                className="block w-full snap-center text-center font-display tracking-wider"
                                style={{
                                    height: ITEM_HEIGHT,
                                    fontSize: isActive ? 36 : 22,
                                    color: isActive ? "#fff" : "rgba(255,255,255,0.35)",
                                    transition: "color 150ms, font-size 150ms",
                                }}
                            >
                                {fmt(v)} {unit}
                            </button>
                        );
                    })}
                </div>
            </div>
            <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>
        </div>
    );
}
